import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

import usePrefix from 'utils/usePrefix';
import { ResqlExpertSubmissionListItem } from 'utils/api/resqlexpert.d';

import NoData from 'components/NoData';
import RowShort from 'components/RowShort';
import SearchBar from 'components/SearchBar';
import HeaderBar from 'components/HeaderBar';
import {
  SubmissionsListStyled,
  SubmissionsListContentStyled,
  SubmissionsSentinelStyled,
  SubmissionsSentinelErrorStyled,
  SubmissionsSentinelRetryStyled,
} from 'components/ResqlExpert/List/styles';

interface Props {
  items: ResqlExpertSubmissionListItem[];
  hasMore: boolean;
  loading: boolean;
  error: Error | null;
  loadMore: () => void;
  retry: () => void;
  selectedId?: number;
  setSelectedId: (id: number) => void;
  query: string;
  setQuery: (q: string) => void;
  headerAction: ReactNode;
  footer?: ReactNode;
}

const SubmissionsListBase: FC<Props> = ({
  items,
  hasMore,
  loading,
  error,
  loadMore,
  retry,
  selectedId,
  setSelectedId,
  query,
  setQuery,
  headerAction,
  footer,
}) => {
  const t = usePrefix('ResqlExpert');

  const handleFilterReset = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    const scroller = scrollerRef.current;
    if (!sentinel || !scroller) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMoreRef.current();
      },
      { root: scroller, rootMargin: '120px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, items.length]);

  const showSentinel = hasMore || loading || !!error;
  const isEmpty = items.length === 0 && !loading && !error;

  return (
    <SubmissionsListStyled>
      <HeaderBar>
        <h2>{t('title')}</h2>
        {headerAction}
      </HeaderBar>
      <SearchBar
        name="filter"
        placeholder={t('search')}
        onChange={(e) => setQuery(e.target.value)}
        value={query}
        onClear={handleFilterReset}
        isDisabled={query.length < 1}
        short
      />
      <SubmissionsListContentStyled ref={scrollerRef} data-testid="submissions-scroller">
        {isEmpty ? (
          <NoData title={t('no_submissions')} />
        ) : (
          <>
            {items.map((item) => (
              <RowShort
                key={item.id}
                title={item.title}
                subtitle={dayjs(item.created_at).format('DD-MM-YYYY')}
                onClick={() => setSelectedId(item.id)}
                selected={selectedId === item.id}
                unread={!item.is_completed}
              />
            ))}
            {showSentinel && (
              <div ref={sentinelRef}>
                {error && !loading ? (
                  <SubmissionsSentinelErrorStyled role="alert">
                    <p>{t('picker_load_error')}</p>
                    <SubmissionsSentinelRetryStyled type="button" onClick={retry}>
                      {t('picker_retry')}
                    </SubmissionsSentinelRetryStyled>
                  </SubmissionsSentinelErrorStyled>
                ) : (
                  <SubmissionsSentinelStyled role="status" aria-live="polite">
                    {t('loading')}
                  </SubmissionsSentinelStyled>
                )}
              </div>
            )}
          </>
        )}
      </SubmissionsListContentStyled>
      {footer}
    </SubmissionsListStyled>
  );
};

export default SubmissionsListBase;
